import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { Email as EmailIcon, Close as CloseIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addDays } from 'date-fns';
import api from '@/services/api';
import { ReleaseNoteItem } from '@/types';

const steps = ['Select Items', 'Configure Release', 'Review & Publish'];

const productSuiteImages: Record<string, string> = {
  'Fresh 360': '/images/green.png',
  'Compliance 360': '/images/red.png',
  'Waste 360': '/images/orange.png',
  'Commerce 360': '/images/purple.png',
  'Inventory 360': '/images/blue.png',
  'General': '/images/grey.png',
};

const getProductSuiteImage = (suite: string) => {
  return productSuiteImages[suite] || productSuiteImages['General'];
};

export default function Publish() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [releaseTypeId, setReleaseTypeId] = useState('');
  const [releaseDate, setReleaseDate] = useState<Date | null>(addDays(new Date(), 7));
  const [description, setDescription] = useState('');
  const [version, setVersion] = useState('');
  
  // Email preview state
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState<string | null>(null);

  // Get pre-selected items from navigation state
  useEffect(() => {
    const state = location.state as { selectedIds?: string[] } | null;
    if (state?.selectedIds) {
      setSelectedIds(new Set(state.selectedIds));
      setActiveStep(1); // Skip to step 2 if items are pre-selected
    }
  }, [location.state]);

  // Fetch available items
  const { data: itemsData, isLoading: itemsLoading } = useQuery({
    queryKey: ['releaseNoteItems', 'forPublish'],
    queryFn: () => api.getReleaseNoteItems({
      excludePublished: true,
      excludeExcluded: true,
    }),
  });

  // Fetch release types
  const { data: releaseTypes, isLoading: releaseTypesLoading, error: releaseTypesError } = useQuery({
    queryKey: ['releaseTypes'],
    queryFn: () => api.getReleaseTypes(),
  });

  // Publish mutation
  const publishMutation = useMutation({
    mutationFn: () => api.publishRelease({
      releaseTypeId,
      releaseDate: releaseDate?.toISOString() || new Date().toISOString(),
      description,
      version: selectedReleaseType?.requiresVersion ? version : undefined,
      itemIds: Array.from(selectedIds),
    }),
    onSuccess: (result) => {
      navigate('/history', { state: { message: `Release published successfully! External ID: ${result.externalId}` } });
    },
  });

  // Email preview mutation
  const emailPreviewMutation = useMutation({
    mutationFn: () => api.previewReleaseEmail({
      releaseTypeId,
      releaseDate: releaseDate?.toISOString() || new Date().toISOString(),
      description,
      version: selectedReleaseType?.requiresVersion ? version : undefined,
      itemIds: Array.from(selectedIds),
    }),
    onSuccess: (html) => {
      setEmailPreviewHtml(html);
      setEmailPreviewOpen(true);
    },
  });

  const items = itemsData?.items || [];
  const selectedItems = items.filter((item) => selectedIds.has(item.id));
  const selectedReleaseType = Array.isArray(releaseTypes) ? releaseTypes.find((rt) => rt.id === releaseTypeId) : undefined;

  // Set default release type
  useEffect(() => {
    if (Array.isArray(releaseTypes) && releaseTypes.length > 0 && !releaseTypeId) {
      const defaultType = releaseTypes.find((rt) => rt.isDefault);
      if (defaultType) {
        setReleaseTypeId(defaultType.id);
      }
    }
  }, [releaseTypes, releaseTypeId]);

  // Update description when release type changes
  useEffect(() => {
    if (selectedReleaseType && releaseDate) {
      let template = selectedReleaseType.descriptionTemplate;
      template = template.replace(/\{\{ReleaseDate\}\}/g, format(releaseDate, 'MMMM dd, yyyy'));
      template = template.replace(/\{\{TodaysDate\}\}/g, format(new Date(), 'MMMM dd, yyyy'));
      template = template.replace(/\{\{ItemCount\}\}/g, selectedIds.size.toString());
      template = template.replace(/\{\{ProductCount\}\}/g, 
        new Set(selectedItems.map(i => i.productSuite).filter(Boolean)).size.toString()
      );
      if (version) {
        template = template.replace(/\{\{Version\}\}/g, version);
      }
      template = template.replace(/\{\{VersionUrl\}\}/g, selectedReleaseType.versionUrl ?? '');
      setDescription(template);
    }
  }, [selectedReleaseType, releaseDate, selectedIds.size, version, selectedItems]);

  const handleSelectItem = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(items.map((item) => item.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return selectedIds.size > 0;
      case 1:
        return releaseTypeId && releaseDate && description && 
          (!selectedReleaseType?.requiresVersion || version);
      case 2:
        return true;
      default:
        return false;
    }
  };

  // Group items by product suite and module for preview
  const groupedItems = selectedItems.reduce((acc, item) => {
    const suite = item.productSuite || 'Unmapped';
    const mod = item.module || 'General';
    if (!acc[suite]) acc[suite] = {};
    if (!acc[suite][mod]) acc[suite][mod] = [];
    acc[suite][mod].push(item);
    return acc;
  }, {} as Record<string, Record<string, ReleaseNoteItem[]>>);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Select Items for Release
            </Typography>
            {itemsLoading ? (
              <CircularProgress />
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={items.length > 0 && selectedIds.size === items.length}
                          indeterminate={selectedIds.size > 0 && selectedIds.size < items.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>Jira Key</TableCell>
                      <TableCell>Summary</TableCell>
                      <TableCell>Product Suite</TableCell>
                      <TableCell>Module</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.has(item.id)}
                            onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>{item.issueKey}</TableCell>
                        <TableCell>{item.summary}</TableCell>
                        <TableCell>{item.productSuite || '-'}</TableCell>
                        <TableCell>{item.module || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Configure Release
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600 }}>
              <FormControl fullWidth>
                <InputLabel>Release Type</InputLabel>
                <Select
                  value={releaseTypeId}
                  label="Release Type"
                  onChange={(e: SelectChangeEvent) => setReleaseTypeId(e.target.value)}
                  disabled={releaseTypesLoading}
                >
                  {releaseTypesLoading ? (
                    <MenuItem value="">Loading...</MenuItem>
                  ) : releaseTypesError ? (
                    <MenuItem value="">Error loading release types</MenuItem>
                  ) : !Array.isArray(releaseTypes) || releaseTypes.length === 0 ? (
                    <MenuItem value="">No release types configured</MenuItem>
                  ) : (
                    releaseTypes.map((rt) => (
                      <MenuItem key={rt.id} value={rt.id}>{rt.name}</MenuItem>
                    ))
                  )}
                </Select>
                {releaseTypesError && (
                  <Typography variant="caption" color="error">
                    {(releaseTypesError as Error)?.message}
                  </Typography>
                )}
              </FormControl>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Release Date"
                  value={releaseDate}
                  onChange={(newValue) => setReleaseDate(newValue)}
                />
              </LocalizationProvider>

              {selectedReleaseType?.requiresVersion && (
                <TextField
                  label="Version Number"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder="e.g., 1.2.3"
                />
              )}

              <TextField
                label="Description"
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Review Release
            </Typography>
            
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">Release Type</Typography>
              <Typography variant="body1">{selectedReleaseType?.name}</Typography>
              
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Release Date</Typography>
              <Typography variant="body1">{releaseDate && format(releaseDate, 'MMMM dd, yyyy')}</Typography>
              
              {selectedReleaseType?.requiresVersion && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Version</Typography>
                  <Typography variant="body1">{version}</Typography>
                </>
              )}
              
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Description</Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{description}</Typography>
            </Paper>

            <Typography variant="h6" sx={{ mb: 2 }}>
              Items ({selectedIds.size})
            </Typography>
            
            {Object.entries(groupedItems).map(([suite, modules]) => (
              <Paper key={suite} sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <img
                    src={getProductSuiteImage(suite)}
                    alt={suite}
                    style={{ width: 32, height: 32 }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <Typography variant="h6" sx={{ color: 'primary.main' }}>
                    {suite}
                  </Typography>
                </Box>
                {Object.entries(modules).map(([mod, modItems]) => (
                  <Box key={mod} sx={{ mb: 2, ml: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {mod}
                    </Typography>
                    {modItems.map((item) => (
                      <Box key={item.id} sx={{ mb: 1, ml: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.issueKey} - {item.summary}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                          {item.currentContent || item.generatedContent}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ))}
              </Paper>
            ))}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Publish Release
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {publishMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error publishing release: {(publishMutation.error as Error)?.message}
        </Alert>
      )}

      {renderStepContent()}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeStep === steps.length - 1 && (
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => emailPreviewMutation.mutate()}
              disabled={emailPreviewMutation.isPending || !canProceed()}
            >
              {emailPreviewMutation.isPending ? 'Loading...' : 'Preview Email'}
            </Button>
          )}
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => publishMutation.mutate()}
              disabled={!canProceed() || publishMutation.isPending}
            >
              {publishMutation.isPending ? 'Publishing...' : 'Publish Release'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>

      {/* Email Preview Dialog */}
      <Dialog
        open={emailPreviewOpen}
        onClose={() => setEmailPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Email Preview
          <IconButton onClick={() => setEmailPreviewOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {emailPreviewHtml && (
            <iframe
              srcDoc={emailPreviewHtml}
              title="Email Preview"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, px: 2 }}>
            This is a preview of the email that will be sent to subscribers when the release is published.
          </Typography>
          <Button onClick={() => setEmailPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Email Preview Error */}
      {emailPreviewMutation.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Error loading email preview: {(emailPreviewMutation.error as Error)?.message}
        </Alert>
      )}
    </Box>
  );
}
